/**
 * HIM-4785 — Product Card Schema.org as pre-rendered JSON-LD.
 *
 * Builds a schema.org `Product` object (emitted as `application/ld+json`) for the
 * product cards, replacing the previous inline HTML microdata (itemprop/itemscope).
 * All string values are HTML-stripped so no markup leaks into the JSON (per the ticket:
 * "Special care should be taken to make sure no HTML tags are stuffed into the JSON code").
 */

// Default product image when a card has none — the DIRECTV logo (per the ticket).
export const DEFAULT_PRODUCT_IMAGE =
  "https://www.directv.com/dtvassets/logos/DIRECTV-1200x1200.png";
export const DEFAULT_PRODUCT_BRAND = "DIRECTV";
export const DEFAULT_PRICE_CURRENCY = "USD";
export const DEFAULT_AVAILABILITY = "https://schema.org/InStock";
export const DIRECTV_ORIGIN = "https://www.directv.com";

// Make a relative CTA path absolute (PRD example uses an absolute offer URL; keeps all card
// types consistent for Google Rich Results). Absolute URLs and non-path values pass through.
const toAbsoluteUrl = (url: string): string => (url.startsWith("/") ? `${DIRECTV_ORIGIN}${url}` : url);

// Named HTML entities the CMS copy uses that aren't numeric references. Kept small and
// explicit (®/™/© and the quote/dash family) rather than pulling in a full entity table.
const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  nbsp: " ",
  quot: '"',
  apos: "'",
  lt: "<",
  gt: ">",
  reg: "®",
  trade: "™",
  copy: "©",
  deg: "°",
  hellip: "…",
  mdash: "—",
  ndash: "–",
  lsquo: "‘",
  rsquo: "’",
  ldquo: "“",
  rdquo: "”"
};

const fromCodePointSafe = (code: number): string => {
  try {
    return Number.isFinite(code) ? String.fromCodePoint(code) : "";
  } catch {
    return "";
  }
};

/**
 * Decode numeric (`&#174;`, `&#xAE;`) and the named HTML entities the CMS emits so schema
 * values read as plain text (® / ™ / '…') instead of raw entity codes.
 */
export const decodeHtmlEntities = (value: string): string => value
  .replace(/&#x([0-9a-f]+);/gi, (_, hex) => fromCodePointSafe(parseInt(hex, 16)))
  .replace(/&#(\d+);/g, (_, dec) => fromCodePointSafe(parseInt(dec, 10)))
  .replace(/&([a-z0-9]+);/gi, (match, name) => (Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, name.toLowerCase()) ?
    NAMED_ENTITIES[name.toLowerCase()] :
    match));

/**
 * Decode entities, strip HTML tags, and collapse whitespace so JSON values are plain text.
 * Entities are decoded first (so `&lt;b&gt;` becomes a real tag) and tags are then removed,
 * so no markup — real or entity-encoded — survives into the JSON.
 */
export const stripHtmlTags = (value?: string | null): string => decodeHtmlEntities(value ?? "")
  .replace(/<[^>]*>/g, " ")
  .replace(/\s+/g, " ")
  .trim();

/**
 * Extract the numeric price (no currency symbol, no "/mo", no HTML) from a CMS price
 * string such as `"$44<sup>99</sup>/mo"` or `"44.99"`. Returns "" when none is found.
 */
export const sanitizePriceForSchema = (
  value?: string | number | null
): string => {
  if (value === null || value === undefined) {
    return "";
  }
  const text = typeof value === "number" ? String(value) : stripHtmlTags(value);
  const match = text.match(/\d+(?:\.\d+)?/);
  return match ? match[0] : "";
};

export interface ProductJsonLdInput {
  /** Product/package name (required — no Product is emitted without it). */
  name?: string;
  description?: string;
  /** Offer URL (the card's CTA target). */
  url?: string;
  /** Product image; falls back to the DIRECTV logo. */
  image?: string;
  brand?: string;
  /** Price string/number; the numeric part is extracted ("$44.99/mo" -> "44.99"). */
  price?: string | number;
  priceCurrency?: string;
  availability?: string;
}

/**
 * Build the schema.org Product object, or null when it would be ineligible.
 *
 * Google's Product rich result REQUIRES one of `offers` / `review` / `aggregateRating`; we only
 * have pricing, so a Product with no usable price is reported as an ERROR by the Rich Results Test
 * (the ticket requires "no errors; warnings are fine"). Rather than publish an invalid Product we
 * return null (the card simply emits no schema) when there is no name OR no real, positive price
 * (a `"0"`/unresolved add-on price is treated as no price).
 */
export const buildProductJsonLd = (
  input: ProductJsonLdInput
): Record<string, any> | null => {
  const name = stripHtmlTags(input.name);
  if (!name) {
    return null;
  }

  const price = sanitizePriceForSchema(input.price);
  if (!price || Number(price) <= 0) {
    return null;
  }

  const product: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    image: stripHtmlTags(input.image) || DEFAULT_PRODUCT_IMAGE,
    brand: {
      "@type": "Brand",
      name: stripHtmlTags(input.brand) || DEFAULT_PRODUCT_BRAND
    }
  };

  const description = stripHtmlTags(input.description);
  if (description) {
    product.description = description;
  }

  const offers: Record<string, any> = {
    "@type": "Offer",
    priceCurrency: stripHtmlTags(input.priceCurrency) || DEFAULT_PRICE_CURRENCY,
    price,
    availability: stripHtmlTags(input.availability) || DEFAULT_AVAILABILITY
  };
  const url = stripHtmlTags(input.url);
  if (url) {
    offers.url = toAbsoluteUrl(url);
  }
  product.offers = offers;

  return product;
};

/**
 * Serialize the Product object for `dangerouslySetInnerHTML`. `<` is escaped to its
 * unicode form so a stray `<` in content can never break out of the `<script>` tag.
 * Returns "" when there is nothing to emit.
 */
export const buildProductJsonLdString = (input: ProductJsonLdInput): string => {
  const product = buildProductJsonLd(input);
  return product ? JSON.stringify(product).replace(/</g, "\\u003c") : "";
};
